import React, { useState } from 'react';
import Header from '../../components/ui/Header';
import PartnerContextHeader from '../../components/ui/PartnerContextHeader';
import OverviewDashboard from './components/OverviewDashboard';
import EnablementOutcomes from './components/EnablementOutcomes';
import TechnicalCapabilityPanel from './components/TechnicalCapabilityPanel';
import SalesCapabilityPanel from './components/SalesCapabilityPanel';
import AICapabilityScoring from './components/AICapabilityScoring';
import TrainingLibrary from './components/TrainingLibrary';
import PartnerTrainingRecords from './components/PartnerTrainingRecords';
import CertificationsTracker from './components/CertificationsTracker';
import CapabilityAssessment from './components/CapabilityAssessment';
import LearningPaths from './components/LearningPaths';
import EnablementReports from './components/EnablementReports';
import eCityToPartnerTraining from './components/eCityToPartnerTraining';
import PartnerToeCityTraining from './components/PartnerToeCityTraining';
import TrainingCalendar from './components/TrainingCalendar';
import TrainingRequests from './components/TrainingRequests';
import CertificationWallet from './components/CertificationWallet';
import Icon from '../../components/AppIcon';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';

const NAV_GROUPS = [
  {
    label: null,
    items: [
      { id: 'overview', label: 'Overview', icon: 'LayoutDashboard', description: 'Dashboard & stats' },
    ]
  },
  {
    label: 'Outcomes',
    items: [
      { id: 'outcomes', label: 'Enablement Outcomes', icon: 'TrendingUp', description: 'Impact & results' },
    ]
  },
  {
    label: 'Training Exchange',
    items: [
      { id: 'ecity-to-partner', label: 'eCity → Partner', icon: 'ArrowRight', description: 'eCity trains partners' },
      { id: 'partner-to-ecity', label: 'Partner → eCity', icon: 'ArrowLeft', description: 'Partners train eCity' },
      { id: 'calendar', label: 'Training Calendar', icon: 'CalendarDays', description: 'Session schedule' },
      { id: 'requests', label: 'Training Requests', icon: 'MessageSquarePlus', description: 'Two-way requests' },
      { id: 'wallet', label: 'Certification Wallet', icon: 'Wallet', description: 'All certificates' },
    ]
  },
  {
    label: 'Enablement Tools',
    items: [
      { id: 'library', label: 'Training Library', icon: 'Library', description: 'Course catalog' },
      { id: 'records', label: 'Training Records', icon: 'GraduationCap', description: 'Partner history' },
      { id: 'certifications', label: 'Certifications', icon: 'Award', description: 'Cert tracker' },
      { id: 'assessment', label: 'Capability Assessment', icon: 'BarChart3', description: 'Radar analysis' },
      { id: 'paths', label: 'Learning Paths', icon: 'Route', description: 'Structured journeys' },
      { id: 'reports', label: 'Reports', icon: 'FileBarChart', description: 'Analytics & export' },
    ]
  },
  {
    label: 'Capability',
    items: [
      { id: 'technical', label: 'Technical', icon: 'Wrench', description: 'Engineering skills' },
      { id: 'sales', label: 'Sales', icon: 'TrendingUp', description: 'Sales capability' },
      { id: 'scoring', label: 'AI Scoring', icon: 'Sparkles', description: 'AI-driven insights' },
    ]
  }
];

const PartnerEnablementCenter = () => {
  const location = useLocation();
  const [activeSection, setActiveSection] = useState(() => location?.state?.tab || 'overview');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [technicalData, setTechnicalData] = useState({
    certifiedEngineers: '12', integrationSkills: 'advanced', hasLab: true, hasDemo: true,
    certifications: [
      { name: 'AWS Solutions Architect Professional', issuer: 'Amazon Web Services', expiryDate: '2026-08-15', status: 'active' },
      { name: 'Cisco CCNP Enterprise', issuer: 'Cisco Systems', expiryDate: '2026-03-10', status: 'active' },
      { name: 'Microsoft Azure Administrator', issuer: 'Microsoft Corporation', expiryDate: '2026-02-20', status: 'active' }
    ]
  });

  const [salesData, setSalesData] = useState({
    salesTeamSize: '8', proposalCapability: '4', dealRegistrationMaturity: 'managed', preSalesSupport: '4',
    hasCRM: true, hasSalesAutomation: true, hasProposalSystem: false, hasSalesAnalytics: true,
    avgDealSize: '125000', winRate: '68', salesCycleDays: '45'
  });

  const aiScore = {
    overall: 76,
    breakdown: [
      { category: 'Technical Capability', score: 82 }, { category: 'Sales Capability', score: 78 },
      { category: 'Training Readiness', score: 68 }, { category: 'Certification Coverage', score: 85 },
      { category: 'Infrastructure Maturity', score: 72 }
    ],
    recommendations: [
      { recommendation: 'Complete Advanced Product Integration Workshop to address critical technical gaps', priority: 'high', impact: 8 },
      { recommendation: 'Implement proposal management system to streamline RFP response process', priority: 'medium', impact: 6 },
      { recommendation: 'Schedule renewal for Microsoft Azure Administrator certification expiring soon', priority: 'high', impact: 5 },
      { recommendation: 'Expand pre-sales team by 2 resources to support growing pipeline', priority: 'medium', impact: 7 }
    ]
  };

  const activeItem = NAV_GROUPS?.flatMap(g => g?.items)?.find(i => i?.id === activeSection);

  return (
    <>
      <Helmet>
        <title>Partner Enablement Center - PartnerHub</title>
        <meta name="description" content="Training exchange, certifications tracker, capability assessments, learning paths, enablement outcomes and reports" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/20 to-cyan-50/10">
        <Header />
        <PartnerContextHeader />

        <main className="pt-8 pb-32">
          <div className="max-w-[1600px] mx-auto px-24 lg:px-32">
            {/* Page Title */}
            <div className="flex items-center gap-12 mb-24">
              <div className="p-12 bg-gradient-to-br from-success to-accent rounded-xl shadow-lg">
                <Icon name="GraduationCap" size={26} className="text-white" />
              </div>
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Partner Enablement Center</h1>
                <p className="text-slate-500 text-sm mt-2">Two-way training exchange, capability outcomes, certifications and assessments</p>
              </div>
            </div>

            {/* Main Layout: Sidebar + Content */}
            <div className="flex gap-20 items-start">
              {/* Left Sidebar Mini-Nav */}
              <aside
                style={{ minWidth: sidebarCollapsed ? '56px' : '256px', width: sidebarCollapsed ? '56px' : '256px' }}
                className={`flex-none shrink-0 transition-all duration-300`}
              >
                <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden sticky top-[200px]">
                  {/* Collapse Toggle */}
                  <div
                    className={`border-b border-slate-100 ${sidebarCollapsed ? 'flex items-center justify-center p-0' : 'flex items-center justify-between px-14 py-12'}`}
                    style={sidebarCollapsed ? { width: '56px', height: '40px' } : {}}
                  >
                    {!sidebarCollapsed && (
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">Navigation</span>
                    )}
                    <button
                      onClick={() => setSidebarCollapsed(p => !p)}
                      className={`rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors flex items-center justify-center ${sidebarCollapsed ? 'p-0' : 'p-6 ml-auto'}`}
                      style={sidebarCollapsed ? { width: '40px', height: '40px', margin: 0, padding: 0 } : {}}
                      title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    >
                      <Icon name={sidebarCollapsed ? 'PanelLeftOpen' : 'PanelLeftClose'} size={16} />
                    </button>
                  </div>

                  {/* Nav Items */}
                  <nav className="py-8">
                    {NAV_GROUPS?.map((group, gi) => (
                      <div key={gi} className={gi > 0 ? 'mt-2' : ''}>
                        {group?.label && !sidebarCollapsed && (
                          <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest px-14 pt-12 pb-6">
                            {group?.label}
                          </p>
                        )}
                        {group?.label && sidebarCollapsed && gi > 0 && (
                          <div className="mx-10 my-6 border-t border-slate-100" />
                        )}
                        {group?.items?.map(item => {
                          const isActive = activeSection === item?.id;
                          return (
                            <button
                              key={item?.id}
                              onClick={() => setActiveSection(item?.id)}
                              title={sidebarCollapsed ? item?.label : undefined}
                              className={`transition-all group relative ${
                                sidebarCollapsed
                                  ? `flex items-center justify-center ${isActive ? 'bg-emerald-50' : 'hover:bg-slate-50'}`
                                  : `w-full flex items-center text-left gap-10 px-14 py-9 ${isActive ? 'bg-emerald-50 text-emerald-700 border-l-[3px] border-emerald-500' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900 border-l-[3px] border-transparent'}`
                              }`}
                              style={sidebarCollapsed ? { width: '56px', height: '40px', padding: 0, margin: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' } : {}}
                            >
                              {sidebarCollapsed ? (
                                <span
                                  className="flex items-center justify-center"
                                  style={{ width: '40px', height: '40px', padding: 0, margin: 0 }}
                                >
                                  <Icon
                                    name={item?.icon}
                                    size={20}
                                    className={`flex-shrink-0 ${isActive ? 'text-emerald-600' : 'text-slate-400 group-hover:text-slate-600'}`}
                                  />
                                </span>
                              ) : (
                                <>
                                  <Icon
                                    name={item?.icon}
                                    size={15}
                                    className={`flex-shrink-0 ${isActive ? 'text-emerald-600' : 'text-slate-400 group-hover:text-slate-600'}`}
                                  />
                                  <span className={`text-xs font-semibold leading-tight ${isActive ? 'text-emerald-700' : 'text-slate-600 group-hover:text-slate-900'}`}>
                                    {item?.label}
                                  </span>
                                </>
                              )}
                              {/* Tooltip for collapsed state */}
                              {sidebarCollapsed && (
                                <div className="absolute left-full ml-8 top-1/2 -translate-y-1/2 bg-slate-900 text-white text-xs font-semibold px-10 py-6 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 shadow-lg">
                                  {item?.label}
                                  <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-slate-900" />
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    ))}
                  </nav>
                </div>
              </aside>

              {/* Main Content Area */}
              <div className="flex-1 min-w-0">
                {/* Section Breadcrumb */}
                {activeItem && (
                  <div className="flex items-center gap-8 mb-16 text-xs text-slate-400">
                    <Icon name="GraduationCap" size={12} />
                    <span>Enablement Center</span>
                    <Icon name="ChevronRight" size={12} />
                    <span className="text-slate-700 font-semibold">{activeItem?.label}</span>
                  </div>
                )}

                {/* Section Content */}
                {activeSection === 'overview' && (
                  <OverviewDashboard onNavigate={setActiveSection} />
                )}
                {activeSection === 'outcomes' && <EnablementOutcomes />}
                {activeSection === 'ecity-to-partner' && <eCityToPartnerTraining />}
                {activeSection === 'partner-to-ecity' && <PartnerToeCityTraining />}
                {activeSection === 'calendar' && <TrainingCalendar />}
                {activeSection === 'requests' && <TrainingRequests />}
                {activeSection === 'wallet' && <CertificationWallet />}
                {activeSection === 'library' && <TrainingLibrary />}
                {activeSection === 'records' && <PartnerTrainingRecords />}
                {activeSection === 'certifications' && <CertificationsTracker />}
                {activeSection === 'assessment' && <CapabilityAssessment />}
                {activeSection === 'paths' && <LearningPaths />}
                {activeSection === 'reports' && <EnablementReports />}
                {activeSection === 'technical' && (
                  <TechnicalCapabilityPanel data={technicalData} onChange={setTechnicalData} />
                )}
                {activeSection === 'sales' && (
                  <SalesCapabilityPanel data={salesData} onChange={setSalesData} />
                )}
                {activeSection === 'scoring' && (
                  <AICapabilityScoring
                    score={aiScore?.overall}
                    breakdown={aiScore?.breakdown}
                    recommendations={aiScore?.recommendations}
                  />
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default PartnerEnablementCenter;